/* eslint-disable @typescript-eslint/no-magic-numbers */

/**
 * Default theme tokens — centralises the values from src/styles
 * so they can be overridden via ThemeConfig.
 */

// ── Spacing (mirrors src/styles size) ──────────────────────────────
export const defaultSpacing = {
  0: '0px',
  4: '4px',
  8: '8px',
  12: '12px',
  16: '16px',
  18: '18px',
  20: '20px',
  24: '24px',
  28: '28px',
  32: '32px',
  36: '36px',
  40: '40px',
  48: '48px',
  54: '54px',
  56: '56px',
  60: '60px',
  64: '64px',
  72: '72px',
  80: '80px',
  88: '88px',
  90: '90px',
  96: '96px',
  104: '104px',
  112: '112px',
  120: '120px',
  128: '128px',
  160: '160px',
} as const;

// ── Border‑radius — Figma TDS rounding scale ──────────────────────────
// ⚠ Visual change: all named radii are larger than the previous scale.
// Verify in Storybook before shipping (see FOUNDATIONS-GAP.md §5).
export const defaultBorderRadius = {
  none: '0px',
  xs: '8px',
  sm: '14px',
  md: '24px',
  lg: '36px',
  xl: '60px',
  '2xl': '80px',
  '3xl': '100px',
  full: '9999px',
  /** @deprecated alias of '2xl' */
  xxl: '80px',
  circle: '50%',
} as const;

// ── Font‑size (mirrors src/styles typography) ─────────────────────
const defaultHtmlFontSize = 16;
const coef = 14 / 14; // defaultFontSize / 14

const pxToRem = (size: number) => `${(size / defaultHtmlFontSize) * coef}rem` as const;

export const defaultHtmlFontSizeMap = {
  xs: 12,
  md: 14,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 40,
} as const;

// TODO: refactor fontSize object keys
export const defaultFontSize = {
  xxxs: pxToRem(12),
  xxs: pxToRem(14),
  xs: pxToRem(16),
  sm: pxToRem(18),
  smMd: pxToRem(20),
  md: pxToRem(22),
  lg: pxToRem(26),
  xl: pxToRem(28),
  xxl: pxToRem(35),
} as const;

// ── Typography — Figma TDS Foundation token map ───────────────────────
// Full per-style definitions: fontSize (px), fontWeight, lineHeight (px | 'auto'),
// letterSpacing (px). Two ramps: App (desktop + mobile) and WEB.
// See FOUNDATIONS-GAP.md §3 for the Figma source values.

export type TypographyStyleDef = {
  fontSize: number;
  fontWeight: 300 | 400 | 500 | 700;
  /** Pixel line-height, or 'auto' for 100%/natural (used in WEB ramp). */
  lineHeight: number | 'auto';
  letterSpacing: number;
};

export type FontWeights = {
  light: 300;
  regular: 400;
  medium: 500;
  bold: 700;
};

export type AppTypography = {
  h1: TypographyStyleDef;
  h1Mobile: TypographyStyleDef;
  h2: TypographyStyleDef;
  h2Mobile: TypographyStyleDef;
  h3: TypographyStyleDef;
  h3Mobile: TypographyStyleDef;
  title: TypographyStyleDef;
  titleMobile: TypographyStyleDef;
  body: TypographyStyleDef;
  bodyMobile: TypographyStyleDef;
  bodySm: TypographyStyleDef;
  bodySmMobile: TypographyStyleDef;
  caption: TypographyStyleDef;
  captionMobile: TypographyStyleDef;
  overline: TypographyStyleDef;
  overlineMobile: TypographyStyleDef;
};

export type WebTypography = {
  h1: TypographyStyleDef;
  h2: TypographyStyleDef;
  h3: TypographyStyleDef;
  h4: TypographyStyleDef;
  titleLight: TypographyStyleDef;
  titleRegular: TypographyStyleDef;
  titleMedium: TypographyStyleDef;
  titleBold: TypographyStyleDef;
  bodyMaxLight: TypographyStyleDef;
  bodyMaxRegular: TypographyStyleDef;
  bodyMaxMedium: TypographyStyleDef;
  bodyMaxBold: TypographyStyleDef;
};

export type Typography = {
  fontFamily: string;
  fontWeight: FontWeights;
  /** App (mobile-first) ramp — desktop and mobile variants. */
  app: AppTypography;
  /** WEB ramp — larger display sizes, lineHeight 'auto'. */
  web: WebTypography;
  /** Optional exact metrics for the public Typography variants. */
  variants?: Partial<Record<
    'h1' | 'h1Mobile' | 'h2' | 'h3' | 'b1' | 'b2' | 'b3' | 'b4' | 'b5' |
    'bodyMedium' | 'bodySmall' | 'bodyMediumMobile',
    { fontSize: string; lineHeight: string; letterSpacing: string; mobileLetterSpacing?: string }
  >>;
};

// App ramp: H1/H2 Bold, H3/Title Medium, Body/Caption/Overline Regular.
// Letter-spacing: 2 for headings + title, 1 for body/caption/overline.
export const defaultTypography: Typography = {
  fontFamily: 'Param',
  fontWeight: { light: 300, regular: 400, medium: 500, bold: 700 },
  app: {
    h1:             { fontSize: 32, fontWeight: 700, lineHeight: 36, letterSpacing: 0.64 },
    h1Mobile:       { fontSize: 28, fontWeight: 700, lineHeight: 32, letterSpacing: 0.56 },
    h2:             { fontSize: 26, fontWeight: 700, lineHeight: 28, letterSpacing: 0.52 },
    h2Mobile:       { fontSize: 22, fontWeight: 700, lineHeight: 26, letterSpacing: 0.44 },
    h3:             { fontSize: 21, fontWeight: 500, lineHeight: 30, letterSpacing: 0.42 },
    h3Mobile:       { fontSize: 18, fontWeight: 500, lineHeight: 20, letterSpacing: 0.36 },
    title:          { fontSize: 18, fontWeight: 500, lineHeight: 24, letterSpacing: 0.36 },
    titleMobile:    { fontSize: 17, fontWeight: 500, lineHeight: 24, letterSpacing: 0.34 },
    body:           { fontSize: 17, fontWeight: 400, lineHeight: 26, letterSpacing: 0.17 },
    bodyMobile:     { fontSize: 15, fontWeight: 400, lineHeight: 20, letterSpacing: 0.15 },
    bodySm:         { fontSize: 14, fontWeight: 400, lineHeight: 20, letterSpacing: 0.14 },
    bodySmMobile:   { fontSize: 13, fontWeight: 400, lineHeight: 18, letterSpacing: 0.13 },
    caption:        { fontSize: 13, fontWeight: 400, lineHeight: 20, letterSpacing: 0.13 },
    captionMobile:  { fontSize: 12, fontWeight: 400, lineHeight: 18, letterSpacing: 0.12 },
    overline:       { fontSize: 11, fontWeight: 400, lineHeight: 16, letterSpacing: 0.11 },
    overlineMobile: { fontSize: 10, fontWeight: 400, lineHeight: 16, letterSpacing: 0.1 },
  },
  web: {
    h1:             { fontSize: 54, fontWeight: 700, lineHeight: 'auto', letterSpacing: 0 },
    h2:             { fontSize: 48, fontWeight: 700, lineHeight: 'auto', letterSpacing: 0 },
    h3:             { fontSize: 38, fontWeight: 700, lineHeight: 'auto', letterSpacing: 0 },
    h4:             { fontSize: 30, fontWeight: 700, lineHeight: 'auto', letterSpacing: 0 },
    titleLight:     { fontSize: 24, fontWeight: 300, lineHeight: 'auto', letterSpacing: 0 },
    titleRegular:   { fontSize: 24, fontWeight: 400, lineHeight: 'auto', letterSpacing: 0 },
    titleMedium:    { fontSize: 24, fontWeight: 500, lineHeight: 'auto', letterSpacing: 0 },
    titleBold:      { fontSize: 24, fontWeight: 700, lineHeight: 'auto', letterSpacing: 0 },
    bodyMaxLight:   { fontSize: 22, fontWeight: 300, lineHeight: 'auto', letterSpacing: 0 },
    bodyMaxRegular: { fontSize: 22, fontWeight: 400, lineHeight: 'auto', letterSpacing: 0 },
    bodyMaxMedium:  { fontSize: 22, fontWeight: 500, lineHeight: 'auto', letterSpacing: 0 },
    bodyMaxBold:    { fontSize: 22, fontWeight: 700, lineHeight: 'auto', letterSpacing: 0 },
  },
};
